<template>
  <div class="participant-controls">
    <v-icon
      v-if="hasRecords"
      color="appMain"
      class="mr-2"
      @click.stop="viewRecords"
    >
      description
    </v-icon>

    <template v-if="isHost && !isParticipantHost">
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <div v-on="on" class="mr-1">
            <v-btn
              fab
              icon
              :x-small="isButtonSize('x-small')"
              :small="isButtonSize('small')"
              @click.stop="toggleMuted"
            >
              <v-icon color="appMain">
                {{ muted ? 'volume_off' : 'volume_up' }}
              </v-icon>
            </v-btn>
          </div>
        </template>
        <span>{{ muted ? 'Unmute' : 'Mute' }}</span>
      </v-tooltip>

      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <div v-on="on" class="mr-1">
            <v-btn
              fab
              icon
              :x-small="isButtonSize('x-small')"
              :small="isButtonSize('small')"
              @click.stop="toggleVideoOff"
            >
              <v-icon color="appMain">
                {{ videoOff ? 'web_asset_off' : 'web_asset' }}
              </v-icon>
            </v-btn>
          </div>
        </template>
        <span>{{ videoOff ? 'Show Video' : 'Hide Video' }}</span>
      </v-tooltip>
    </template>

    <div v-if="isParticipantHost">
      <v-icon v-if="!activeTracks.audio" color="error" class="mr-2"
        >mic_off</v-icon
      >
      <v-icon v-if="!activeTracks.video" color="error" class="mr-2"
        >videocam_off</v-icon
      >
    </div>

    <dropdown-menu-button :size="buttonSize" v-else>
      <v-list>
        <template v-if="isBreakoutRoom">
          <v-list-item @click="inviteToMainRoom">
            <v-list-item-title> Move back to main room </v-list-item-title>
          </v-list-item>

          <v-list-item @click="finishInterview('end')">
            <v-list-item-title> End Interview </v-list-item-title>
          </v-list-item>
        </template>

        <v-list-item @click="prioritizeParticipant">
          <v-list-item-title> Move to top </v-list-item-title>
        </v-list-item>

        <v-list-item @click="sendParticipantMessage">
          <v-list-item-title> Send direct message </v-list-item-title>
        </v-list-item>

        <v-list-item
          v-if="isHost && !isBreakoutRoom"
          @click="finishInterview('ban')"
        >
          <v-list-item-title> Ban Participant </v-list-item-title>
        </v-list-item>
      </v-list>
    </dropdown-menu-button>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { eventBus } from '@/lib/events';
import analyticSender from '@/components/rooms/helpers/analyticSender';

export default {
  name: 'ParticipantControls',
  props: {
    participant: Object,
    activeTracks: Object,
    small: Boolean,
  },
  computed: {
    ...mapState('rooms', ['roomInfo']),
    ...mapGetters('rooms', ['isHost', 'getParticipantRecords']),
    buttonSize() {
      return this.small ? 'x-small' : 'small';
    },

    participantRecords() {
      return this.getParticipantRecords(this.participantId);
    },

    participantId() {
      return this.participant.identity;
    },

    hasRecords() {
      return !!this.participantRecords;
    },

    isParticipantHost() {
      return this.participantId.includes('host');
    },

    muted() {
      return this.participant.muted;
    },

    videoOff() {
      return this.participant.video_off;
    },

    isBreakoutRoom() {
      return this.roomInfo.room.includes('-breakout');
    },
  },
  methods: {
    ...mapActions('rooms', ['createMeetingToken']),

    isButtonSize(size) {
      return this.buttonSize === size;
    },

    updateInfo(info = {}, options = {}) {
      info.identity = info.identity || this.participant.identity;
      eventBus.trigger('participantInfoUpdated', {
        info,
        options,
      });
    },

    viewRecords() {
      eventBus.trigger('showParticipantAnswers', {
        participantId: this.participantId,
      });
    },

    toggleMuted() {
      const muted = !this.muted;
      this.updateInfo({ muted }, { to: 'host' });
      this.saveEvent(muted ? 'muted' : 'unmuted');
    },

    toggleVideoOff() {
      const video_off = !this.videoOff;
      this.updateInfo({ video_off }, { to: 'host' });
      this.saveEvent(video_off ? 'video_off' : 'video_on');
    },

    saveEvent(event) {
      const participant_identity = this.participantId.replace('host_', '');
      analyticSender(`${event}_participant`, {
        participant_identity,
      });
    },

    async inviteToMainRoom() {
      const room = (this.roomInfo.room || '').split('-breakout')[0];
      const roomName = localStorage.getItem(room) || 'Main Room';

      eventBus.trigger('moveGuestToMainRoom', {
        room,
        roomName,
        participant: this.participant,
      });
    },

    prioritizeParticipant() {
      eventBus.trigger('prioritizeParticipant', this.participantId);
    },

    sendParticipantMessage() {
      eventBus.trigger('sendParticipantMessage', this.participantId);
    },

    finishInterview(reason) {
      const banParticipant = reason === 'ban';

      let text, title;
      if (banParticipant) {
        title = 'Ban Participant';
        text =
          'Are you sure you want to remove this applicant from the interview?';
      } else {
        title = 'End Interview';
        text =
          'Are you sure you want to finish the interview with this applicant?';
      }

      this.openConfirmationModal({
        title,
        buttonText: 'Confirm',
        text: `${text}<br> They won’t be able to rejoin with their existing link.`,
        onConfirm: () => {
          this.$emit('sendCustomAction', {
            type: 'ActionEndInterview',
            to: { identity: this.participantId },
            reason,
          });
        },
      });
    },
  },
};
</script>

<style>
.participant-controls {
  display: flex;
}
</style>
